/**
 * The dialogs behind a staff row's actions menu.
 *
 * Each one is a thin form over an endpoint that already exists and already
 * carries the rules. Editing the record is `PATCH /staff/:id`; giving somebody
 * a login, changing their role or switching their access off is the members
 * API, which is where the role hierarchy, the owner protection and "you cannot
 * grant what you do not hold" live. Reimplementing any of that here would mean
 * two sets of rules to keep in step.
 */

import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import {
  EMPLOYMENT_STATUSES,
  EMPLOYMENT_STATUS_LABELS,
  MEMBER_ROLES,
  MEMBER_ROLE_LABELS,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  type EmploymentStatus,
  type MemberRole,
  type Permission,
  type Staff,
} from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { Badge, Button, Input, Modal, Select } from '../../components/ui';

interface Branch {
  id: string;
  name: string;
}

/** Field errors from the API, flattened to one message per field. */
function useFormErrors(): {
  error: string | null;
  fieldErrors: Record<string, string>;
  reset: () => void;
  capture: (caught: unknown, fallback: string) => void;
} {
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  return {
    error,
    fieldErrors,
    reset: () => {
      setError(null);
      setFieldErrors({});
    },
    capture: (caught: unknown, fallback: string) => {
      if (caught instanceof ApiError) {
        setError(caught.message);
        if (caught.fields) {
          setFieldErrors(
            Object.fromEntries(
              Object.entries(caught.fields).map(([key, messages]) => [key, messages[0] ?? '']),
            ),
          );
        }
      } else {
        setError(fallback);
      }
    },
  };
}

function Alert({ message }: { message: string }): ReactNode {
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
      <p className="text-sm text-red-800">{message}</p>
    </div>
  );
}

// ── Edit ────────────────────────────────────────────────────────────────────

/**
 * Everything about the record itself, on one form.
 *
 * Changing the status here has the same consequence as using Deactivate —
 * somebody not employed does not keep a working login — which the API applies
 * to the transition rather than to the button. The hint says so, because a
 * dropdown that quietly revokes a password should admit to it.
 */
export function EditStaffDialog({
  member,
  branches,
  onClose,
  onSaved,
}: {
  member: Staff;
  branches: Branch[];
  onClose: () => void;
  onSaved: (message: string) => void;
}): ReactNode {
  const path = useBusinessPath();
  const { business } = useAuth();
  const { error, fieldErrors, reset, capture } = useFormErrors();

  const [form, setForm] = useState({
    fullName: member.fullName,
    roleTitle: member.roleTitle ?? '',
    phone: member.phone ?? '',
    salaryAmount: member.salaryAmount ?? '',
    hiredAt: member.hiredAt ?? '',
    employmentStatus: member.employmentStatus,
    branchId: member.branchId ?? '',
  });

  const update =
    <K extends keyof typeof form>(key: K) =>
    (event: { target: { value: string } }) =>
      setForm((current) => ({ ...current, [key]: event.target.value }));

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.patch<Staff>(path(`/staff/${member.id}`), payload),
    onSuccess: () => onSaved(`${form.fullName.trim() || member.fullName} updated`),
    onError: (caught) => capture(caught, 'Could not save the changes.'),
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    reset();
    save.mutate({
      fullName: form.fullName,
      roleTitle: form.roleTitle || undefined,
      phone: form.phone || null,
      salaryAmount: form.salaryAmount || null,
      hiredAt: form.hiredAt || null,
      employmentStatus: form.employmentStatus,
      branchId: form.branchId || null,
    });
  }

  const losesAccess =
    member.employmentStatus === 'active' &&
    form.employmentStatus !== 'active' &&
    member.memberStatus === 'active';

  return (
    <Modal open onClose={onClose} title={`Edit ${member.fullName}`} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && Object.keys(fieldErrors).length === 0 ? <Alert message={error} /> : null}

        <Input
          label="Full name"
          required
          autoFocus
          value={form.fullName}
          onChange={update('fullName')}
          error={fieldErrors.fullName}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Role / job title"
            value={form.roleTitle}
            onChange={update('roleTitle')}
            error={fieldErrors.roleTitle}
            placeholder="e.g. Rider, Cook, Cashier"
          />
          <Input
            label="Phone number"
            type="tel"
            value={form.phone}
            onChange={update('phone')}
            error={fieldErrors.phone}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label={`Salary (${business?.currencyCode ?? ''})`}
            inputMode="decimal"
            value={form.salaryAmount}
            onChange={update('salaryAmount')}
            error={fieldErrors.salaryAmount}
            hint="Leave empty if not on a salary"
          />
          <Input
            label="Hire date"
            type="date"
            value={form.hiredAt}
            onChange={update('hiredAt')}
            error={fieldErrors.hiredAt}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Status"
            options={EMPLOYMENT_STATUSES.map((value) => ({
              value,
              label: EMPLOYMENT_STATUS_LABELS[value],
            }))}
            value={form.employmentStatus}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                employmentStatus: event.target.value as EmploymentStatus,
              }))
            }
            hint={losesAccess ? 'This will also switch off their dashboard access.' : undefined}
          />
          <Select
            label="Assigned location"
            value={form.branchId}
            onChange={update('branchId')}
            placeholder="No location"
            options={branches.map((branch) => ({ value: branch.id, label: branch.name }))}
            error={fieldErrors.branchId}
            hint="Where this person works"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Salary ──────────────────────────────────────────────────────────────────

/** The one field, for when that is all somebody came to change. */
export function SalaryDialog({
  member,
  onClose,
  onSaved,
}: {
  member: Staff;
  onClose: () => void;
  onSaved: (message: string) => void;
}): ReactNode {
  const path = useBusinessPath();
  const { business } = useAuth();
  const { error, fieldErrors, reset, capture } = useFormErrors();
  const [amount, setAmount] = useState(member.salaryAmount ?? '');

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.patch<Staff>(path(`/staff/${member.id}`), payload),
    onSuccess: () => onSaved(`Salary updated for ${member.fullName}`),
    onError: (caught) => capture(caught, 'Could not update the salary.'),
  });

  return (
    <Modal open onClose={onClose} title={`Update salary — ${member.fullName}`}>
      <form
        className="space-y-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          reset();
          save.mutate({ salaryAmount: amount || null });
        }}
      >
        {error && Object.keys(fieldErrors).length === 0 ? <Alert message={error} /> : null}

        <Input
          label={`Salary (${business?.currencyCode ?? ''})`}
          inputMode="decimal"
          autoFocus
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          error={fieldErrors.salaryAmount}
          hint="Clear the field to record no salary"
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            Save salary
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Login & access ──────────────────────────────────────────────────────────

/**
 * Whether this person can sign in, and as what.
 *
 * Two states: no login yet, which is an invitation; or a login, whose role can
 * change and whose access can be switched off without ending their employment
 * — a cashier who no longer needs the dashboard is still a cashier.
 *
 * Deactivating the person does both at once. This is the finer control.
 */
export function AccessDialog({
  member,
  onClose,
  onSaved,
}: {
  member: Staff;
  onClose: () => void;
  onSaved: (message: string) => void;
}): ReactNode {
  const path = useBusinessPath();
  const { can, user } = useAuth();
  const { error, fieldErrors, reset, capture } = useFormErrors();

  const [email, setEmail] = useState(member.email ?? '');
  const [role, setRole] = useState<MemberRole>(member.memberRole ?? 'staff');

  const isSelf = Boolean(member.userId && user?.id === member.userId);
  const assignable = MEMBER_ROLES.filter((r) => r !== 'owner' && r !== 'location_operator' && r !== 'integration');

  const invite = useMutation({
    mutationFn: () => api.post(path('/members'), { email, role, fullName: member.fullName }),
    onSuccess: () => onSaved(`${email} invited as ${MEMBER_ROLE_LABELS[role]}`),
    onError: (caught) => capture(caught, 'Could not send the invitation.'),
  });

  const change = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.patch(path(`/members/${member.memberId}`), payload),
    onSuccess: (_result, payload) =>
      onSaved(
        (payload as { status?: string }).status === 'suspended'
          ? `Dashboard access switched off for ${member.fullName}`
          : (payload as { status?: string }).status === 'active'
            ? `Dashboard access restored for ${member.fullName}`
            : `${member.fullName} is now ${MEMBER_ROLE_LABELS[role]}`,
      ),
    onError: (caught) => capture(caught, 'Could not change their access.'),
  });

  const busy = invite.isPending || change.isPending;

  if (!member.memberId) {
    return (
      <Modal open onClose={onClose} title={`Set login & access — ${member.fullName}`}>
        <form
          className="space-y-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            reset();
            invite.mutate();
          }}
        >
          {error && Object.keys(fieldErrors).length === 0 ? <Alert message={error} /> : null}

          <p className="text-sm text-slate-600">
            {member.fullName} has no login. Inviting them sends a link to set a password; their
            staff record and anything attributed to it stays as it is.
          </p>

          <Input
            label="Email address"
            type="email"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={fieldErrors.email}
            hint="Where the invitation goes. This is also how their login is matched to this record."
          />

          <Select
            label="Role"
            options={assignable.map((value) => ({ value, label: MEMBER_ROLE_LABELS[value] }))}
            value={role}
            onChange={(event) => setRole(event.target.value as MemberRole)}
            error={fieldErrors.role}
            hint="What they can reach. Fine-tune it afterwards with Manage permissions."
          />

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={invite.isPending} disabled={!can('members:invite')}>
              Send invitation
            </Button>
          </div>
        </form>
      </Modal>
    );
  }

  const suspended = member.memberStatus === 'suspended';

  return (
    <Modal open onClose={onClose} title={`Set login & access — ${member.fullName}`}>
      <div className="space-y-4">
        {error ? <Alert message={error} /> : null}

        <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-900">
              {member.email ?? 'Has a login'}
            </p>
            <p className="text-xs text-slate-500">
              {member.memberStatus === 'invited'
                ? 'Invitation not accepted yet'
                : suspended
                  ? 'Access switched off'
                  : 'Can sign in'}
            </p>
          </div>
          <Badge
            tone={member.memberStatus === 'active' ? 'success' : suspended ? 'neutral' : 'warning'}
          >
            {member.memberRole ? MEMBER_ROLE_LABELS[member.memberRole] : 'No role'}
          </Badge>
        </div>

        <Select
          label="Role"
          options={assignable.map((value) => ({ value, label: MEMBER_ROLE_LABELS[value] }))}
          value={role}
          onChange={(event) => setRole(event.target.value as MemberRole)}
          hint="Saved when you choose Change role."
        />

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-3">
          {member.memberStatus === 'invited' ? (
            <span className="text-xs text-slate-500">
              Access can be switched off once the invitation is accepted.
            </span>
          ) : (
            <Button
              type="button"
              variant="secondary"
              disabled={busy || isSelf}
              onClick={() => {
                reset();
                change.mutate({ status: suspended ? 'active' : 'suspended' });
              }}
            >
              {suspended ? 'Restore access' : 'Switch off access'}
            </Button>
          )}

          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              type="button"
              loading={change.isPending}
              disabled={busy || role === member.memberRole}
              onClick={() => {
                reset();
                change.mutate({ role });
              }}
            >
              Change role
            </Button>
          </div>
        </div>

        {isSelf ? (
          <p className="text-xs text-slate-500">
            This is your own login, so you cannot switch your own access off.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

// ── Permissions ─────────────────────────────────────────────────────────────

interface MemberRecord {
  id: string;
  role: MemberRole;
  permissions: { grant?: Permission[]; revoke?: Permission[] } | null;
}

/**
 * What this person can do, on top of or short of their role.
 *
 * The role is the baseline and is shown as such; the overrides are the only
 * thing this writes. Permissions are listed by their own names rather than
 * invented friendly labels — the same choice the location login screen makes,
 * and the only honest one while the names are what the API enforces.
 *
 * Granting something the signed-in person does not themselves hold is refused
 * by the API, not hidden here, so the reason comes back in words.
 */
export function PermissionsDialog({
  member,
  onClose,
  onSaved,
}: {
  member: Staff;
  onClose: () => void;
  onSaved: (message: string) => void;
}): ReactNode {
  const path = useBusinessPath();
  const { error, reset, capture } = useFormErrors();
  const [filter, setFilter] = useState('');
  const [overrides, setOverrides] = useState<{ grant: Permission[]; revoke: Permission[] } | null>(
    null,
  );

  const { data, isPending } = useQuery({
    queryKey: ['members', 'for-staff', member.memberId],
    queryFn: () => api.get<{ data: MemberRecord[] }>(path('/members?pageSize=200')),
    enabled: Boolean(member.memberId),
  });

  const record = data?.data.find((row) => row.id === member.memberId);
  const base = useMemo(
    () => new Set<Permission>(record ? ROLE_PERMISSIONS[record.role] : []),
    [record],
  );

  const current =
    overrides ?? {
      grant: record?.permissions?.grant ?? [],
      revoke: record?.permissions?.revoke ?? [],
    };

  const save = useMutation({
    mutationFn: () =>
      api.patch(path(`/members/${member.memberId}`), {
        permissions:
          current.grant.length === 0 && current.revoke.length === 0
            ? null
            : { grant: current.grant, revoke: current.revoke },
      }),
    onSuccess: () => onSaved(`Permissions updated for ${member.fullName}`),
    onError: (caught) => capture(caught, 'Could not save the permissions.'),
  });

  /** Inherit → granted/revoked → inherit, depending on the role's baseline. */
  function toggle(permission: Permission): void {
    const granted = current.grant.includes(permission);
    const revoked = current.revoke.includes(permission);
    const next = {
      grant: current.grant.filter((p) => p !== permission),
      revoke: current.revoke.filter((p) => p !== permission),
    };

    if (base.has(permission)) {
      if (!revoked) next.revoke = [...next.revoke, permission];
    } else if (!granted) {
      next.grant = [...next.grant, permission];
    }

    setOverrides(next);
  }

  const shown = PERMISSIONS.filter((permission) =>
    filter ? permission.toLowerCase().includes(filter.toLowerCase()) : true,
  );

  return (
    <Modal open onClose={onClose} title={`Manage permissions — ${member.fullName}`} size="lg">
      <div className="space-y-4">
        {error ? <Alert message={error} /> : null}

        {isPending ? (
          <p className="text-sm text-slate-500">Loading their access…</p>
        ) : !record ? (
          <p className="text-sm text-slate-600">
            This staff member has no login, so there is nothing to permit yet. Use Set login &amp;
            access first.
          </p>
        ) : (
          <>
            <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
              <ShieldCheck className="mt-0.5 size-4 shrink-0 text-slate-400" aria-hidden />
              <span>
                Their <strong>{MEMBER_ROLE_LABELS[record.role]}</strong> role already allows{' '}
                {base.size} of these. Changing one here records an exception to the role, so the
                role can still be changed underneath it.
              </span>
            </div>

            <Input
              label="Find a permission"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="e.g. expenses, deliveries:delete"
            />

            <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
              {shown.map((permission) => {
                const granted = current.grant.includes(permission);
                const revoked = current.revoke.includes(permission);
                const allowed = granted || (base.has(permission) && !revoked);

                return (
                  <li
                    key={permission}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <span className="min-w-0 flex-1 truncate font-mono text-xs text-slate-600">
                      {permission}
                    </span>
                    {granted ? (
                      <Badge tone="success">Granted</Badge>
                    ) : revoked ? (
                      <Badge tone="danger">Revoked</Badge>
                    ) : (
                      <Badge tone={allowed ? 'info' : 'neutral'}>
                        {allowed ? 'From role' : 'Not allowed'}
                      </Badge>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => toggle(permission)}
                    >
                      {granted || revoked ? 'Reset' : allowed ? 'Revoke' : 'Grant'}
                    </Button>
                  </li>
                );
              })}
            </ul>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                loading={save.isPending}
                disabled={overrides === null}
                onClick={() => {
                  reset();
                  save.mutate();
                }}
              >
                Save permissions
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
